import React, {
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";
import expertDefaultImg from "../../images/expert-default.png";
import logoWhite from "../../images/logo_white.png";
import industryLogo from "../../images/default_industry.png";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { UrlContext } from "../../contexts/UrlContext";
import ScreeningQuestionsAnswer from "./screeningQuestionsAnswer";
import { DayPilot } from "@daypilot/daypilot-lite-react";
import RadarChart from "./radarChart";
import Swal from "sweetalert2";

const ProfilePrintContent = forwardRef(function ProfilePrintContent(
  {
    expertID,
    projectID,
    profile,
    projectType,
    renderScope = "default",
    clickingOnProfile,
    markProfilesAsCallRequested,
  },
  ref
) {
  const { API_URL } = useContext(UrlContext);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const [profileExperience, setProfileExperience] = useState([]);
  const [dimension, setDimension] = useState([]);
  const [events, setEvents] = useState([]);
  const profileName = projectType === "exp" ? "EPH Expert" : "EPH Consultant";
  const expertPosition = profile?.expertPosition || "Independent Consultant";

  const profilePicturePreview = profile?.expertProfilePicture?.includes(
    mediaLink
  )
    ? profile.expertProfilePicture
    : `${databaseLink}${profile?.expertProfilePicture || ""}`;

  const location = profile?.expertLocation || "";
  const languages = profile?.language || "";
  const profileEducation = profile?.expertEducation || [];
  const rateCurrency = profile?.projectCurrency;
  const rateValue = profile?.expertDailyRate;
  const rateFrequency = profile?.expertBillingFrequency;
  const ephExpert = projectType === "exp" ? "EPH_EXP_" : "EPH_CON_";
  const availabilityComment = profile?.expertAvailableComment || "Fully available";
  const profileSummary = profile?.expertSum || "";
	const [selectedSlots, setSelectedSlots] = useState([]);
	const [loading, setLoading] = useState(false);
  const [showRangeSlotPopup, setShowRangeSlotPopup] = useState(false);
  const [activeRangeSlot, setActiveRangeSlot] = useState(null);
  const [pendingSpecificSlot, setPendingSpecificSlot] = useState(null);
  const [refinedRangeSlots, setRefinedRangeSlots] = useState({});

	const toggleSelectedSlot = (slotId) => {
		if (!slotId) return;
		setSelectedSlots((prev) =>
			prev.includes(slotId)
				? prev.filter((id) => id !== slotId)
				: [...prev, slotId]
		);
	};


  const requestedCallDurationMinutes = projectType === "exp" ? 60 : 30;

  // Treat the stored values as timezone-neutral wall-clock values.
  // ConsultantTimeSlot stores strings such as "2026-09-14T07:00:00".
  // Using UTC getters/setters here avoids changing the displayed/stored clock
  // time when the client happens to be in another browser timezone.
  const parseWallClockDate = (value) => {
    const rawValue = String(value || "").trim().slice(0, 19);
    const match = rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
    );

    if (!match) return null;

    const [, year, month, day, hour, minute, second = "00"] = match;

    return new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    );
  };

  const formatWallClockDateTime = (date) => {
    const pad = (value) => String(value).padStart(2, "0");

    return [
      date.getUTCFullYear(),
      "-",
      pad(date.getUTCMonth() + 1),
      "-",
      pad(date.getUTCDate()),
      "T",
      pad(date.getUTCHours()),
      ":",
      pad(date.getUTCMinutes()),
      ":",
      pad(date.getUTCSeconds()),
    ].join("");
  };

  const formatRefinedSlotDate = (value) => {
    const date = parseWallClockDate(value);
    if (!date) return "";

    return new Intl.DateTimeFormat("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  const formatRefinedSlotTime = (value) => {
    const date = parseWallClockDate(value);
    if (!date) return "";

    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    }).format(date);
  };

  const getStoredSlotStart = (event) =>
    event?.originalStartTime ||
    event?.databaseStartTime ||
    event?.startTime?.toString?.() ||
    String(event?.startTime || "");

  const getStoredSlotEnd = (event) =>
    event?.originalEndTime ||
    event?.databaseEndTime ||
    event?.endTime?.toString?.() ||
    String(event?.endTime || "");

  const getSlotDurationMinutes = (event) => {
    const start = parseWallClockDate(getStoredSlotStart(event));
    const end = parseWallClockDate(getStoredSlotEnd(event));

    if (!start || !end) return 0;

    return (end.getTime() - start.getTime()) / 60000;
  };

  const isRangeSlot = (event) =>
    getSlotDurationMinutes(event) > requestedCallDurationMinutes + 0.01;

  const buildSpecificSlots = (event) => {
    const start = parseWallClockDate(getStoredSlotStart(event));
    const end = parseWallClockDate(getStoredSlotEnd(event));

    if (!start || !end || end <= start) return [];

    const durationMs = requestedCallDurationMinutes * 60 * 1000;
    const options = [];

    for (
      let cursor = start.getTime();
      cursor + durationMs <= end.getTime();
      cursor += durationMs
    ) {
      const specificStart = new Date(cursor);
      const specificEnd = new Date(cursor + durationMs);

      options.push({
        slotID: event.id,
        startTime: formatWallClockDateTime(specificStart),
        endTime: formatWallClockDateTime(specificEnd),
      });
    }

    return options;
  };

  const openRangeSlotPopup = (event) => {
    if (!event || event.isCallRequested || loading) return;

    setActiveRangeSlot(event);
    setPendingSpecificSlot(null);
    setShowRangeSlotPopup(true);
  };

  const restoreOriginalRangeSlot = (slotId) => {
    setEvents((previousEvents) =>
      previousEvents.map((event) => {
        if (String(event.id) !== String(slotId)) return event;

        const originalStart = event.originalStartTime || event.databaseStartTime;
        const originalEnd = event.originalEndTime || event.databaseEndTime;

        if (!originalStart || !originalEnd) return event;

        return {
          ...event,
          databaseStartTime: originalStart,
          databaseEndTime: originalEnd,
          startTime: new DayPilot.Date(originalStart),
          endTime: new DayPilot.Date(originalEnd),
          start: new DayPilot.Date(originalStart),
          end: new DayPilot.Date(originalEnd),
          text: eventToString({
            ...event,
            startTime: originalStart,
            endTime: originalEnd,
          }),
        };
      })
    );
  };

  const handleAvailabilitySlotClick = (event) => {
    if (!event?.id || event.isCallRequested || loading) return;

    const alreadySelected = selectedSlots.includes(event.id);

    if (alreadySelected) {
      setSelectedSlots((previous) =>
        previous.filter((id) => String(id) !== String(event.id))
      );

      if (refinedRangeSlots[String(event.id)]) {
        setRefinedRangeSlots((previous) => {
          const next = { ...previous };
          delete next[String(event.id)];
          return next;
        });
        restoreOriginalRangeSlot(event.id);
      }
      return;
    }

    if (isRangeSlot(event)) {
      openRangeSlotPopup(event);
      return;
    }

    setSelectedSlots((previous) => [...previous, event.id]);
  };

  const prepareCallRequest = () => {
    if (!selectedSlots.length || loading) return;

    // Every broad range is refined at the moment the user clicks it.
    // This is a defensive fallback in case a selected range somehow has no
    // exact child slot yet.
    const unresolvedRange = events.find(
      (event) =>
        selectedSlots.includes(event.id) &&
        isRangeSlot(event) &&
        !refinedRangeSlots[String(event.id)]
    );

    if (unresolvedRange) {
      openRangeSlotPopup(unresolvedRange);
      return;
    }

    const slotUpdates = selectedSlots
      .map((slotID) => refinedRangeSlots[String(slotID)])
      .filter(Boolean)
      .map((selected) => ({
        id: selected.slotID,
        startTime: selected.startTime,
        endTime: selected.endTime,
      }));

    requestForCall(slotUpdates);
  };

  const chooseRefinedRangeSlot = (specificSlot) => {
    setPendingSpecificSlot(specificSlot);
  };

  const confirmRangeSlotSelection = () => {
    if (!activeRangeSlot || !pendingSpecificSlot || loading) return;

    const slotID = activeRangeSlot.id;
    const selected = {
      ...pendingSpecificSlot,
      slotID,
    };

    setRefinedRangeSlots((previous) => ({
      ...previous,
      [String(slotID)]: selected,
    }));

    setEvents((previousEvents) =>
      previousEvents.map((event) =>
        String(event.id) === String(slotID)
          ? {
              ...event,
              databaseStartTime: selected.startTime,
              databaseEndTime: selected.endTime,
              startTime: new DayPilot.Date(selected.startTime),
              endTime: new DayPilot.Date(selected.endTime),
              start: new DayPilot.Date(selected.startTime),
              end: new DayPilot.Date(selected.endTime),
              text: eventToString({
                ...event,
                startTime: selected.startTime,
                endTime: selected.endTime,
              }),
            }
          : event
      )
    );

    setSelectedSlots((previous) =>
      previous.includes(slotID) ? previous : [...previous, slotID]
    );

    setShowRangeSlotPopup(false);
    setActiveRangeSlot(null);
    setPendingSpecificSlot(null);
  };

  const closeRangeSlotPopup = () => {
    if (loading) return;

    setShowRangeSlotPopup(false);
    setActiveRangeSlot(null);
    setPendingSpecificSlot(null);
  };

  const eventToString = (selectedEvent) => {
    const selectedTimeSlot = {
      weekday: new Intl.DateTimeFormat("en-GB", { weekday: "short" }).format(
        new Date(selectedEvent.startTime)
      ),
      day: new Intl.DateTimeFormat("en-GB", { day: "2-digit" }).format(
        new Date(selectedEvent.startTime)
      ),
      month: new Intl.DateTimeFormat("en-GB", { month: "short" }).format(
        new Date(selectedEvent.startTime)
      ),
      year: new Intl.DateTimeFormat("en-GB", { year: "numeric" }).format(
        new Date(selectedEvent.startTime)
      ),
      startTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(selectedEvent.startTime)),
      endTime: new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(selectedEvent.endTime)),
    };

    return `${selectedTimeSlot.weekday}, ${selectedTimeSlot.day} ${selectedTimeSlot.month} ${selectedTimeSlot.year} ${selectedTimeSlot.startTime} - ${selectedTimeSlot.endTime} (${selectedEvent.timezone})`;
  };

  const getExpertExperience = async () => {
    const requestBody = {
      expertID,
      powerPointID: profile?.ID,
    };

    try {
      const response = await fetch(`${API_URL}fetch-power-point-experience`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        setProfileExperience(responseData.expertExperience || []);
      } else {
        console.error("Error fetching expert experience:", responseData.text);
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
    }
  };

  const getProjectDimensions = async () => {
    const requestBody = { projectID };

    try {
      const response = await fetch(`${API_URL}get-current-project-dimensions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const fetchedDimensions = [
          responseData.dimensions?.dimension1,
          responseData.dimensions?.dimension2,
          responseData.dimensions?.dimension3,
          responseData.dimensions?.dimension4,
          responseData.dimensions?.dimension5,
        ];

        setDimension(fetchedDimensions);
      } else {
        console.error("Failed to get dimensions");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  // API to fetch timeslots for today and future dates in ascending orders.
  const getMeetingSlots = async () => {
    const requestBody = {
      projectID,
      expertID,
    };

    try {
      const response = await fetch(`${API_URL}get-expert-meeting-time-slot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (responseData.status === "success") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const formattedEvents = responseData.eventData
          .filter((event) => {
            const eventDate = new Date(event.startTime);
            eventDate.setHours(0, 0, 0, 0);

            return eventDate >= today;
          })
          .sort(
            (a, b) =>
              new Date(a.startTime).getTime() -
              new Date(b.startTime).getTime()
          )
          .map((event) => ({
            id: event.id,
            eventID: event.eventID,
            expertID: event.expertID,
            projectID: event.projectID,
            timezone: event.timezone,
            originalStartTime: event.startTime,
            originalEndTime: event.endTime,
            databaseStartTime: event.startTime,
            databaseEndTime: event.endTime,
            startTime: new DayPilot.Date(event.startTime),
            endTime: new DayPilot.Date(event.endTime),
            start: new DayPilot.Date(event.startTime),
            end: new DayPilot.Date(event.endTime),
            text: eventToString(event),
            isCallRequested: event.isCallRequested,
            callRequestDate: event.callRequestDate,
          }));

        setEvents(formattedEvents);
      } else {
        console.error("Failed to get meeting slots");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

	// Request the selected availability slots for a client call.
	// Broad availability ranges can optionally be narrowed first; those
	// corrections are sent as slotUpdates and persisted by the backend.
	const requestForCall = async (slotUpdates = []) => {
		if (!selectedSlots.length || loading) return;

		setLoading(true);

		const requestBody = {
			projectID,
			expertID,
			projectType,
			client: false,
			selectedSlots,
			slotUpdates,
		};

		try {
			const modifiedUrl = "request-for-call";
			const response = await fetch(`${API_URL}${modifiedUrl}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			});

			const responseData = await response.json();

			if (responseData.status === "success") {
				clickingOnProfile(expertID, "Client requested a call.");

				const currentTime = new Date().toISOString().slice(0, 19);
				const updatesBySlotID = new Map(
					(slotUpdates || []).map((update) => [String(update.id), update])
				);

				setEvents((prevEvents) =>
					prevEvents.map((event) => {
						if (!selectedSlots.includes(event.id)) return event;

						const update = updatesBySlotID.get(String(event.id));

						if (!update) {
							return {
								...event,
								isCallRequested: true,
								callRequestDate: currentTime,
							};
						}

						return {
							...event,
							databaseStartTime: update.startTime,
							databaseEndTime: update.endTime,
							startTime: new DayPilot.Date(update.startTime),
							endTime: new DayPilot.Date(update.endTime),
							start: new DayPilot.Date(update.startTime),
							end: new DayPilot.Date(update.endTime),
							text: eventToString({
								...event,
								startTime: update.startTime,
								endTime: update.endTime,
							}),
							isCallRequested: true,
							callRequestDate: currentTime,
						};
					})
				);

				// Keep SharedProfiles as the single source of truth so the row
				// immediately flips from "Request call" to "Cancel request"
				// after a request made from ProfilePrintContent.
				markProfilesAsCallRequested?.([expertID], true);

				setSelectedSlots([]);
				setShowRangeSlotPopup(false);
        setActiveRangeSlot(null);
        setPendingSpecificSlot(null);
				setRefinedRangeSlots({});

				Swal.fire({
					title: "Thank you!",
					text: "We will schedule a call as soon as possible.",
					icon: "success",
					confirmButtonText: "OK",
					timer: 5000,
					timerProgressBar: true,
				});
			} else {
				Swal.fire({
					title: "Error!",
					text: responseData.text || "Error while requesting call.",
					icon: "error",
					confirmButtonText: "OK",
					timer: 2500,
					timerProgressBar: true,
				});
			}
		} catch (error) {
			console.error("Error while requesting call:", error);

			Swal.fire({
				title: "Error!",
				text: "Error while requesting call.",
				icon: "error",
				confirmButtonText: "OK",
				timer: 2500,
				timerProgressBar: true,
			});
		} finally {
			setLoading(false);
		}
	};

  useEffect(() => {
    if (!expertID) return;

    getExpertExperience();
    getProjectDimensions();
    getMeetingSlots();
  }, [expertID, projectID]);

  function useBubbleCount() {
    const [count, setCount] = useState(0);

    useEffect(() => {
      const updateCount = () => {
        const width = window.innerWidth;
        const bubbleCount = Math.max(8, Math.floor(width / 52));

        setCount(bubbleCount);
      };

      updateCount();
      window.addEventListener("resize", updateCount);

      return () => window.removeEventListener("resize", updateCount);
    }, []);

    return count;
  }

  const baseCount = useBubbleCount();
  const rowOffsets = [0, 2, 3];

  const getRowCount = (rowIndex) => {
    return Math.max(baseCount - (rowOffsets[rowIndex] || 0), 0);
  };

  const renderSummarySection = () => (
    <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-bold print-text-lg">EPH Summary</h2>
      </div>

      <div
        className="prose prose-sm text-gray-700 text-sm leading-relaxed print-text-sm [&_h3]:text-md [&_h3]:mt-2 [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
        dangerouslySetInnerHTML={{ __html: profileSummary }}
      />
    </div>
  );

  const renderSpiderChartSection = () => {
    if (!profile?.hasMatching) return null;

    return (
      <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
        <h2 className="text-xl font-bold mb-2 sm:mb-0 print-text-lg">
          EPH Requirement Matching
        </h2>

        <div className="flex flex-col sm:flex-row gap-6 items-center justify-between">
          <div className="w-full lg:w-5/12">
            {[1, 2, 3, 4, 5].map((num) => {
              const key = num - 1;

              return (
                <div
                  key={num}
                  className="flex items-start gap-2 text-sm text-[#365561] print-text-sm"
                >
                  <span className="shrink-0">{num}:</span>
                  <span className="leading-5 break-words">
                    {dimension?.[key] || "-"}
                  </span>
                </div>
              );
            })}
          </div>

          <RadarChart
            values={profile?.chartConfig?.value || []}
            labels={["1", "2", "3", "4", "5"]}
            valueBadge={profile?.chartConfig?.badgeValue}
            size={850}
            renderSize={150}
          />
        </div>
      </div>
    );
  };

  const renderScreeningQuestionsSection = () => (
    <ScreeningQuestionsAnswer
      project_id={projectID}
      expert_id={profile?.expertID}
      profile={profile}
    />
  );

  const renderExperienceSection = () => (
		<motion.div
			key="experience-view"
			layout
			initial={{ x: 0, opacity: 1 }}
			animate={{ x: 0, opacity: 1 }}
			exit={{ x: "100%", opacity: 0 }}
			transition={{ type: "spring", stiffness: 500, damping: 30 }}
			className="bg-white rounded-xl shadow p-6 relative print-experience-split print-no-shadow print-border"
		>
      <h2 className="text-xl font-bold mb-6 print-text-lg">
        Relevant Experience
      </h2>

      {profileExperience?.length > 0 ? (
				<div className="space-y-2 print-experience-list">
					{profileExperience.map((exp, index) => (
						<div
							key={exp.id || index}
							className="relative w-full rounded-md transition duration-200 px-2 py-1 select-none print-experience-item"
						>
              {exp.subExperienceBlocks?.length > 0 ? (
                <>
                  <div className="flex ml-1 items-start gap-4">
                    {exp.companyLogo ? (
                      <img
                        src={
                          exp.companyLogo.includes("/pictures/")
                            ? `https://images.expertpowerhouse.com${exp.companyLogo}`
                            : exp.companyLogo
                        }
                        alt="Company Logo"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = industryLogo;
                        }}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                      />
                    ) : (
                      <img
                        src={industryLogo}
                        alt="Default Industry Logo"
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                      />
                    )}

                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base print-text-md">
                        {exp.companyName}
                      </h3>
                      <p className="text-sm text-gray-500 print-text-sm">
                        {exp.duration}
                      </p>
                    </div>
                  </div>

                  <ul className="list-none mt-2 ml-9 pl-4 relative">
                    {exp.subExperienceBlocks.map((role, j) => (
                      <li
                        key={j}
                        className={`relative pl-2 ${
                          j === exp.subExperienceBlocks.length - 1
                            ? ""
                            : "mb-3"
                        }`}
                      >
                        <div className="absolute -left-[35px] top-2 w-2 h-2 bg-gray-400 rounded-full z-10" />

                        {j !== exp.subExperienceBlocks.length - 1 && (
                          <div className="absolute -left-[32px] top-5 h-full w-px bg-gray-300 z-0" />
                        )}

                        <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                          {role.designation}
                        </h4>

                        <p className="text-xs text-gray-500 print-text-sm">
                          {role.joiningDate} {role.leavingDate ? "-" : null}{" "}
                          {role.leavingDate} {role.duration ? "·" : null}{" "}
                          {role.duration}
                        </p>

                        {role.location && (
                          <p className="text-xs text-gray-500 print-text-sm">
                            {role.location}
                          </p>
                        )}

                        {role.description && (
                          <p className="mt-1 text-sm text-gray-700 whitespace-pre-line print-text-sm">
                            {role.description}
                          </p>
                        )}

                        {role.skills && (
                          <p className="mt-2 text-xs text-gray-600 font-medium print-text-sm">
                            💡{" "}
                            {Array.isArray(role.skills)
                              ? role.skills.join(", ")
                              : role.skills}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="ml-1 flex gap-4">
                  {exp.companyLogo ? (
                    <img
                      src={
                        exp.companyLogo.includes("/pictures/")
                          ? `https://images.expertpowerhouse.com${exp.companyLogo}`
                          : exp.companyLogo
                      }
                      alt="Company Logo"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = industryLogo;
                      }}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border"
                    />
                  ) : (
                    <img
                      src={industryLogo}
                      alt="Default Industry Logo"
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-contain border bg-gray-100"
                    />
                  )}

                  <div>
                    <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                      {exp.designation}
                    </h4>

                    <p className="text-sm text-gray-500 print-text-sm">
                      {exp.companyName}
                    </p>

                    <p className="text-xs text-gray-500 print-text-sm">
                      {exp.joiningDate} {exp.leavingDate ? "-" : null}{" "}
                      {exp.leavingDate} {exp.duration ? "·" : null}{" "}
                      {exp.duration}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 print-text-sm">
          No experience data available.
        </p>
      )}
    </motion.div>
  );

  const renderEducationSection = () => (
    <div className="bg-white rounded-xl p-6 relative transition-all duration-300 shadow print-card-outer print-no-shadow print-border">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xl font-bold print-text-lg">
          Education & Certification
        </h2>
      </div>

      {profileEducation?.length > 0 ? (
        <div className="space-y-4">
          {profileEducation.map((block, index) => {
            const lines = block.split("\n").filter(Boolean);

            return (
              <div key={index}>
                <h4 className="text-gray-800 font-medium text-sm sm:text-base print-text-md">
                  {lines[0]}
                </h4>

                {lines.slice(1).length > 0 && (
                  <p className="text-sm text-gray-500 print-text-sm">
                    {lines.slice(1).join(" · ")}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 print-text-sm">
          No education data available.
        </p>
      )}
    </div>
  );

  const renderHeaderSection = () => (
    <div className="relative bg-white rounded-xl shadow-sm z-40 print-safe-reset">
      <div className="print-banner relative h-24 sm:h-36 bg-cover bg-top bg-[24%_0%] sm:bg-[23%_0%] rounded-xl print-rounded-none">
        <div className="absolute inset-x-0 top-0 h-24 sm:h-36 rounded-t-xl bg-gradient-to-r from-[#365561] via-[#2f4a55] to-[#0f172a]" />

        {[0, 1, 2].map((row) => (
          <div
            key={row}
            style={{ "--row": row }}
            className="
              print-hide-bubbles
              absolute inset-0 flex flex-wrap 
              justify-end gap-3 sm:gap-4 px-4 opacity-80
              top-[calc(24px+var(--row)*24px)]
              sm:top-[calc(24px+var(--row)*40px)]
            "
          >
            {[...Array(getRowCount(row))].map((_, i) => {
              const isHighlight = i % 7 === 0 || i % 11 === 0;

              return (
                <div
                  key={i}
                  className={`rounded-full ${
                    isHighlight ? "bg-[#2dd4bf]" : "bg-[#3b6a75]"
                  } w-[10px] h-[10px] sm:w-[16px] sm:h-[16px]`}
                />
              );
            })}
          </div>
        ))}

        <div className="absolute top-2 left-4 sm:top-4 sm:left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
          <img
            src={logoWhite}
            alt="EPH Logo"
            className="w-[90px] sm:w-[180px] print-eph-logo"
          />
        </div>
      </div>

      <div className="px-4 sm:px-6 md:px-8 print-px-reset">
        <div className="relative flex flex-col items-left text-left -mt-20 sm:-mt-24 print-mt-reset">
          <div className="mt-2 flex flex-col mb-4">
            <div className="flex flex-col">
              <div className="relative w-24 h-24 sm:w-36 sm:h-36 mt-4 print-profile-image print-profile-side">
                <div className="relative group mx-auto w-24 h-24 sm:w-36 sm:h-36 mb-4 bg-gray-100 rounded-full border-4 border-white text-gray-400 print-profile-image">
                  <img
                    src={profilePicturePreview || expertDefaultImg}
                    alt="Preview"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = expertDefaultImg;
                    }}
                    className="w-full h-full rounded-full border-5 border-white object-cover object-[center_15%] shadow-md"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-start md:justify-between items-start lg:items-center gap-4 print-header-row">
              <div className="flex flex-col print-header-left print-info-side">
                <div className="flex flex-row items-center justify-start gap-2">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 no-underline mb-1 print-text-xl">
                    {profile?.isShareName
                      ? profileName
                      : `${ephExpert}${profile?.expertDigitalID || ""}`}
                  </p>
                </div>

                <div className="flex flex-col gap-2 text-sm sm:text-md mb-2 print-text-md">
                  <div className="flex flex-col">
                    <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md mb-0.5">
                      <span className="leading-5">💼</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {expertPosition}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-[400px] mb-0.5">
                      <span className="leading-5">🌍 </span>
                      <span className="leading-5 break-words whitespace-normal">
                        {location}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-xs sm:max-w-sm md:max-w-md mb-0.5">
                      <span className="leading-5">🗣️</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {languages || "No information"}
                      </span>
                    </div>

                    <div className="flex items-start gap-1 max-w-[400px] mb-0.5">
                      <span className="leading-5">📆</span>
                      <span className="leading-5 break-words whitespace-normal">
                        {availabilityComment}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-start md:justify-end mb-4 print-rate-wrap print-rate-side">
                <div className="bg-[#365561] text-white rounded-xl px-6 py-4 shadow-lg min-w-[200px] print-no-shadow print-rate-card">
                  <div className="text-[10px] sm:text-xs uppercase tracking-wide opacity-80">
                    rate
                  </div>

									<span className="inline-flex items-baseline gap-2 uppercase">
										<p className="mt-1 text-xl sm:text-3xl font-bold leading-tight print-text-xl">{rateCurrency} {rateValue}</p>
										<p className="text-[10px] sm:text-xs opacity-80">{rateFrequency}</p>
									</span>

									<p className="mt-1 text-[10px] sm:text-xs opacity-80">incl. of all EPH fees</p>
                </div>
              </div>
            </div>

            {events.length > 0 && (
              <div
                className="flex flex-col items-start gap-1 print-availability"
              >
                <span className="pt-1 text-sm text-gray-500 font-semibold">Availability for Call (Europe/Berlin):</span>
                
                <div className="grid grid-cols-[repeat(auto-fill,85px)] gap-1.5 w-full bg-white availability-grid">
                  {events.map((event, index) => {
                    const startDate = new Date(event.startTime);
                    const endDate = new Date(event.endTime);

                    const weekday = new Intl.DateTimeFormat("en-GB", {
                      weekday: "short",
                    }).format(startDate);

                    const day = new Intl.DateTimeFormat("en-GB", {
                      day: "2-digit",
                    }).format(startDate);

                    const month = new Intl.DateTimeFormat("en-GB", {
                      month: "short",
                    }).format(startDate);

                    const yearShort = new Intl.DateTimeFormat("en-GB", {
                      year: "2-digit",
                    }).format(startDate);

                    const startTime = new Intl.DateTimeFormat("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }).format(startDate);

                    const endTime = new Intl.DateTimeFormat("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    }).format(endDate);

                    const isSelected = selectedSlots.includes(event.id);

                    return (
                      <div
                        key={event.id || index}
                        onClick={event.isCallRequested ? undefined : () => handleAvailabilitySlotClick(event)}
                        className={`
                          availability-card relative
                          w-[85px] h-[82px]
                          min-w-[72px] max-w-[85px]
                          overflow-hidden rounded-xl
                          border bg-[#365561]/5
                          shadow-sm
                          text-left cursor-pointer
                          transition-all duration-150
                          hover:-translate-y-0.5 hover:shadow-md

                          ${
                            isSelected
                              ? "border-[#365561] ring-2 ring-[#365561]/30"
                              : "border-[#365561]/20"
                          }
                        `}
                        aria-pressed={isSelected}
                      >
                        <div className="bg-[#365561] px-1 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-white availability-card-top">
                          {month} {yearShort}
                        </div>

                        <div className="px-2 py-1 text-center availability-card-body">
                          <div className="text-base font-bold leading-none text-[#365561]">
                            {day}
                          </div>

                          <div className="mt-0.5 text-[10px] font-semibold text-gray-700 availability-weekday">
                            {weekday}
                          </div>

                          <div className="mt-0.5 truncate text-[10px] text-gray-500 availability-time">
                            {startTime} - {endTime}
                          </div>
                        </div>

                        {/* {(isSelected || event.isCallRequested) && (
                          <div className="slot-selected-overlay no-print">
                            <div className="slot-selected-check">
                              ✓
                            </div>
                          </div>
                        )} */}

                        {isSelected && (
                          <div className="slot-selected-overlay no-print">
                            <div className="slot-selected-check">
                              ✓
                            </div>
                          </div>
                        )}
                        {event.isCallRequested && (
                          <div className="slot-selected-overlay no-print">
                            <div className="text-white/60 text-xs font-semibold -rotate-45 whitespace-nowrap">
                              Call Requested
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedSlots.length > 0 && (
                  <button
                    type="button"
                    className="mt-2 px-4 py-2 rounded-xl bg-[#365561]/20 no-print"
                    onClick={prepareCallRequest}
                    disabled={loading}
                  >
                    {loading ? <i className="fa-solid fa-spinner fa-spin" /> : "Request call"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderScreenLayout = () => (
    <div className="pt-4 sm:pt-36 pb-10 grid grid-cols-1 lg:grid-cols-6 gap-6 print-single-column print-gap-reset print-pt-reset print-px-reset print-stack-gap">
      <section className="col-span-3 space-y-6">
        {renderSummarySection()}
        {renderSpiderChartSection()}
        {renderScreeningQuestionsSection()}
      </section>

      <aside className="col-span-3 space-y-6">
        {renderExperienceSection()}
        {renderEducationSection()}
      </aside>
    </div>
  );

  const renderPrintLayout = () => (
    <div className="pt-4 pb-10 space-y-4 print-pt-reset print-px-reset print-stack-gap">
      {renderSummarySection()}
      {renderExperienceSection()}
      {renderEducationSection()}
      {renderScreeningQuestionsSection()}
      {renderSpiderChartSection()}
    </div>
  );

  const renderRangeSlotPopup = () => {
    if (
      !showRangeSlotPopup ||
      !activeRangeSlot ||
      typeof document === "undefined"
    ) {
      return null;
    }

    const options = buildSpecificSlots(activeRangeSlot);
    const originalStart =
      activeRangeSlot.originalStartTime || getStoredSlotStart(activeRangeSlot);
    const originalEnd =
      activeRangeSlot.originalEndTime || getStoredSlotEnd(activeRangeSlot);

    return createPortal(
      <div
        className="fixed inset-0 z-[10080] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm no-print"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeRangeSlotPopup();
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 340, damping: 30 }}
          className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.30)]"
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-4 py-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-bold text-slate-900">
                  Choose exact call time
                </h3>
                <span className="rounded-full bg-[#365561]/10 px-2.5 py-1 text-[11px] font-bold text-[#365561]">
                  {requestedCallDurationMinutes} min
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {formatRefinedSlotDate(originalStart)} · {formatRefinedSlotTime(originalStart)} – {formatRefinedSlotTime(originalEnd)}
                {activeRangeSlot.timezone ? ` (${activeRangeSlot.timezone})` : ""}
              </p>
            </div>

            <button
              type="button"
              onClick={closeRangeSlotPopup}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-transparent text-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close exact time selection"
            >
              ×
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/70 p-4 sm:p-6">
            <p className="mb-4 text-sm leading-5 text-slate-600">
              Select one {requestedCallDurationMinutes}-minute slot from this availability range.
            </p>

            {options.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {options.map((option) => {
                  const isSelected =
                    pendingSpecificSlot?.startTime === option.startTime &&
                    pendingSpecificSlot?.endTime === option.endTime;

                  return (
                    <button
                      key={`${activeRangeSlot.id}-${option.startTime}`}
                      type="button"
                      onClick={() => chooseRefinedRangeSlot(option)}
                      className={[
                        "relative flex min-h-[76px] flex-col items-center justify-center rounded-xl border px-3 py-2 text-center transition focus:outline-none",
                        isSelected
                          ? "border-[#365561] bg-[#365561] text-white shadow-[0_10px_24px_rgba(54,85,97,0.20)]"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#365561]/35 hover:bg-[#365561]/5",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "text-[10px] font-semibold uppercase tracking-[0.08em]",
                          isSelected ? "text-white/70" : "text-slate-400",
                        ].join(" ")}
                      >
                        {formatRefinedSlotDate(option.startTime)}
                      </span>
                      <span className="mt-1 text-sm font-bold">
                        {formatRefinedSlotTime(option.startTime)} – {formatRefinedSlotTime(option.endTime)}
                      </span>
                      {isSelected && (
                        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white/20 text-[11px] font-bold text-white">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                This availability range is shorter than the required {requestedCallDurationMinutes}-minute call duration.
              </div>
            )}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 bg-white px-4 py-4">
            <button
              type="button"
              onClick={closeRangeSlotPopup}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!pendingSpecificSlot}
              onClick={confirmRangeSlotSelection}
              className="inline-flex min-w-[150px] items-center justify-center rounded-xl border-0 bg-[#365561] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2e4a54] disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Use selected time
            </button>
          </div>
        </motion.div>
      </div>,
      document.body
    );
  };

  return (
    <>
    <div ref={ref} className="print-root bg-none">
      <style>
				{`
					.profile-print-only {
						display: none;
					}

					.slot-selected-overlay {
						position: absolute;
						inset: 0;
						z-index: 30;
						display: flex;
						align-items: center;
						justify-content: center;
						background: rgba(54, 85, 97, 0.24);
						pointer-events: none;
					}

					.slot-selected-check {
						width: 30px;
						height: 30px;
						border-radius: 999px;
						display: flex;
						align-items: center;
						justify-content: center;
						background: rgba(54, 85, 97, 0.50);
						// color: #ffffff;
						color: rgb(245, 247, 247, 0.6);
						font-size: 19px;
						line-height: 1;
						font-weight: 800;
						box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);
					}

					@media print {
						.profile-screen-only {
							display: none !important;
						}

						.profile-print-only {
							display: block !important;
						}

						.no-print {
							display: none !important;
						}
					}
				`}
			</style>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8">
        {renderHeaderSection()}

        <div className="profile-screen-only">{renderScreenLayout()}</div>

        <div className="profile-print-only">{renderPrintLayout()}</div>
      </div>
    </div>

    {renderRangeSlotPopup()}
    </>
  );
});

export default ProfilePrintContent;